/**
 * SystemPromptEditor Component
 *
 * Admin component for System-Prompt management:
 * - Create, edit, delete system prompts
 * - Markdown editor with syntax highlighting
 * - Prompts are assigned to agents via AgentForm
 *
 * @module components/admin/SystemPromptEditor
 */

import { useState, useCallback, useMemo, memo, useEffect, useRef } from 'react';
import {
  FileText,
  Search,
  Loader2,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  Save,
  X,
  Pencil,
  ScrollText,
  Bot
} from 'lucide-react';
import MarkdownCodeEditor from '../shared/MarkdownCodeEditor';

// ============================================
// Constants
// ============================================

const API_BASE = '/api';

// ============================================
// API Functions
// ============================================

async function fetchSystemPrompts() {
  const res = await fetch(`${API_BASE}/system-prompts`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch system prompts');
  return res.json();
}

async function createSystemPrompt(data) {
  const res = await fetch(`${API_BASE}/system-prompts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to create system prompt');
  }
  return res.json();
}

async function updateSystemPrompt(id, data) {
  const res = await fetch(`${API_BASE}/system-prompts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to update system prompt');
  }
  return res.json();
}

async function deleteSystemPrompt(id) {
  const res = await fetch(`${API_BASE}/system-prompts/${id}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to delete system prompt');
  }
  return { success: true };
}

// ============================================
// Sub-Components
// ============================================

const StatusMessage = memo(function StatusMessage({ type, message, onDismiss }) {
  const isError = type === 'error';

  return (
    <div
      className={`
        flex items-center justify-between p-3 rounded-lg
        ${isError ? 'bg-red-500/10 border border-red-500/30' : 'bg-green-500/10 border border-green-500/30'}
      `}
    >
      <div className="flex items-center gap-2">
        {isError ? (
          <AlertCircle className="w-5 h-5 text-red-400" />
        ) : (
          <CheckCircle className="w-5 h-5 text-green-400" />
        )}
        <span className={isError ? 'text-red-300' : 'text-green-300'}>
          {message}
        </span>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 hover:bg-gray-700 rounded transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      )}
    </div>
  );
});

/**
 * CreatePromptDialog - Dialog for creating new system prompts
 */
const CreatePromptDialog = memo(function CreatePromptDialog({
  isOpen,
  onConfirm,
  onCancel,
  isProcessing
}) {
  const [name, setName] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && name.trim() && !isProcessing) {
      onConfirm(name.trim());
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-indigo-400" />
            Create System Prompt
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <label className="block text-sm text-gray-400 mb-2">
            Prompt Name
          </label>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g., frontend-developer"
            disabled={isProcessing}
            className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
          />
          <p className="mt-2 text-xs text-gray-500">
            Unique name for this system prompt. Used for identification.
          </p>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => name.trim() && onConfirm(name.trim())}
            disabled={isProcessing || !name.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Create
          </button>
        </div>
      </div>
    </div>
  );
});

/**
 * ConfirmDeleteDialog - Confirmation dialog for deleting prompts
 */
const ConfirmDeleteDialog = memo(function ConfirmDeleteDialog({
  isOpen,
  promptName,
  agentCount,
  onConfirm,
  onCancel,
  isProcessing
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-400" />
            Delete System Prompt
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-gray-300">
            Are you sure you want to delete <span className="font-semibold text-white">{promptName}</span>?
          </p>
          {agentCount > 0 && (
            <div className="mt-3 flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <p className="text-sm text-yellow-300">
                This prompt is assigned to {agentCount} agent{agentCount > 1 ? 's' : ''}.
                They will have their system prompt disabled.
              </p>
            </div>
          )}
          <p className="mt-3 text-sm text-gray-500">
            This action cannot be undone.
          </p>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isProcessing}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors disabled:opacity-50"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
});

/**
 * RenameDialog - Dialog for renaming prompts
 */
const RenameDialog = memo(function RenameDialog({
  isOpen,
  currentName,
  onConfirm,
  onCancel,
  isProcessing
}) {
  const [name, setName] = useState(currentName || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setName(currentName || '');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, currentName]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && name.trim() && !isProcessing) {
      onConfirm(name.trim());
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Pencil className="w-5 h-5 text-indigo-400" />
            Rename System Prompt
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <label className="block text-sm text-gray-400 mb-2">
            New Name
          </label>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="prompt-name"
            disabled={isProcessing}
            className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
          />
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => name.trim() && onConfirm(name.trim())}
            disabled={isProcessing || !name.trim() || name.trim() === currentName}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        </div>
      </div>
    </div>
  );
});

/**
 * PromptListItem - Single item in the prompt list
 */
const PromptListItem = memo(function PromptListItem({
  prompt,
  isSelected,
  onSelect,
  onDelete,
  onRename,
  isProcessing
}) {
  return (
    <div
      className={`
        flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors
        ${isSelected
          ? 'bg-indigo-600/20 border-indigo-500/50'
          : 'bg-gray-800/50 border-gray-700/50 hover:border-gray-600'
        }
      `}
      onClick={() => onSelect(prompt)}
    >
      {/* Icon */}
      <ScrollText className="w-5 h-5 text-amber-400 flex-shrink-0" />

      {/* Name and metadata */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-200 truncate" title={prompt.name}>
          {prompt.name}
        </div>
        {prompt._count?.agents > 0 && (
          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
            <Bot className="w-3 h-3" />
            <span>{prompt._count.agents} agent{prompt._count.agents > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRename(prompt);
          }}
          disabled={isProcessing}
          className="p-1.5 text-gray-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded transition-colors disabled:opacity-50"
          title="Rename"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(prompt);
          }}
          disabled={isProcessing}
          className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
});

/**
 * PromptList - List of all system prompts
 */
const PromptList = memo(function PromptList({
  prompts,
  selectedPrompt,
  onSelect,
  onDelete,
  onRename,
  searchQuery,
  isProcessing
}) {
  const filteredPrompts = useMemo(() => {
    if (!searchQuery) return prompts;
    const query = searchQuery.toLowerCase();
    return prompts.filter(p => p.name.toLowerCase().includes(query));
  }, [prompts, searchQuery]);

  if (filteredPrompts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <ScrollText className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>{searchQuery ? 'No matching prompts' : 'No system prompts yet'}</p>
        <p className="text-xs mt-1">Create one to get started</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {filteredPrompts.map(prompt => (
        <PromptListItem
          key={prompt.id}
          prompt={prompt}
          isSelected={selectedPrompt?.id === prompt.id}
          onSelect={onSelect}
          onDelete={onDelete}
          onRename={onRename}
          isProcessing={isProcessing}
        />
      ))}
    </div>
  );
});

/**
 * PromptEditor - Markdown editor for prompt content
 */
const PromptEditor = memo(function PromptEditor({
  prompt,
  onChange,
  onSave,
  isSaving,
  hasChanges
}) {
  if (!prompt) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <ScrollText className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Select a system prompt to edit</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Editor Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <ScrollText className="w-5 h-5 text-amber-400" />
          <span className="font-medium text-white">{prompt.name}</span>
          {hasChanges && (
            <span className="text-xs text-yellow-400">(unsaved changes)</span>
          )}
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving || !hasChanges}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save
        </button>
      </div>

      {/* Editor Body */}
      <div className="flex-1 p-4 overflow-hidden">
        <MarkdownCodeEditor
          value={prompt.content || ''}
          onChange={(value) => onChange({ ...prompt, content: value || '' })}
          placeholder="Enter system prompt content..."
          height="100%"
        />
      </div>

      {/* Editor Footer - Help text */}
      <div className="px-4 py-3 border-t border-gray-700 bg-gray-800/50">
        <p className="text-xs text-gray-500">
          This prompt will be pushed to the agent's host as <code className="px-1 py-0.5 bg-gray-700 rounded">~/.claude/SYSTEM-PROMPT-EXT.md</code> and loaded via <code className="px-1 py-0.5 bg-gray-700 rounded">--append-system-prompt</code> on spawn.
        </p>
      </div>
    </div>
  );
});

// ============================================
// Main Component
// ============================================

export default function SystemPromptEditor({ className = '' }) {
  // State
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [editedPrompt, setEditedPrompt] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [statusMessage, setStatusMessage] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, prompt: null });
  const [renameDialog, setRenameDialog] = useState({ isOpen: false, prompt: null });

  // Load prompts on mount
  useEffect(() => {
    loadPrompts();
  }, []);

  // Sync edited prompt when selection changes
  useEffect(() => {
    setEditedPrompt(selectedPrompt ? { ...selectedPrompt } : null);
  }, [selectedPrompt]);

  // Check for unsaved changes
  const hasChanges = useMemo(() => {
    if (!selectedPrompt || !editedPrompt) return false;
    return selectedPrompt.content !== editedPrompt.content;
  }, [selectedPrompt, editedPrompt]);

  // API Handlers
  const loadPrompts = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchSystemPrompts();
      setPrompts(result.data || []);
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!editedPrompt) return;
    setIsSaving(true);
    try {
      const result = await updateSystemPrompt(editedPrompt.id, {
        content: editedPrompt.content
      });
      // Handle both { data: {...} } and direct object response formats
      const updated = result.data || result;
      setSelectedPrompt(updated);
      setPrompts(prev => prev.map(p => p.id === updated.id ? updated : p));
      setStatusMessage({ type: 'success', message: 'System prompt saved' });
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsSaving(false);
    }
  }, [editedPrompt]);

  // Create handlers
  const handleCreateClick = useCallback(() => {
    setCreateDialog(true);
  }, []);

  const handleCreateConfirm = useCallback(async (name) => {
    setIsProcessing(true);
    try {
      const result = await createSystemPrompt({
        name: name.toLowerCase().replace(/\s+/g, '-'),
        content: `# ${name}\n\nEnter your system prompt content here.`
      });
      // Handle both { data: {...} } and direct object response formats
      const created = result.data || result;
      setPrompts(prev => [...prev, created]);
      setSelectedPrompt(created);
      setStatusMessage({ type: 'success', message: 'System prompt created' });
      setCreateDialog(false);
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleCreateCancel = useCallback(() => {
    setCreateDialog(false);
  }, []);

  // Delete handlers
  const handleDeleteClick = useCallback((prompt) => {
    setDeleteDialog({ isOpen: true, prompt });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    const prompt = deleteDialog.prompt;
    if (!prompt) return;

    setIsProcessing(true);
    try {
      await deleteSystemPrompt(prompt.id);
      setPrompts(prev => prev.filter(p => p.id !== prompt.id));
      if (selectedPrompt?.id === prompt.id) {
        setSelectedPrompt(null);
      }
      setStatusMessage({ type: 'success', message: 'System prompt deleted' });
      setDeleteDialog({ isOpen: false, prompt: null });
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [deleteDialog.prompt, selectedPrompt]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialog({ isOpen: false, prompt: null });
  }, []);

  // Rename handlers
  const handleRenameClick = useCallback((prompt) => {
    setRenameDialog({ isOpen: true, prompt });
  }, []);

  const handleRenameConfirm = useCallback(async (newName) => {
    const prompt = renameDialog.prompt;
    if (!prompt) return;

    setIsProcessing(true);
    try {
      await updateSystemPrompt(prompt.id, { name: newName });
      setPrompts(prev => prev.map(p => p.id === prompt.id ? { ...p, name: newName } : p));
      if (selectedPrompt?.id === prompt.id) {
        setSelectedPrompt(prev => prev ? { ...prev, name: newName } : null);
      }
      setStatusMessage({ type: 'success', message: 'System prompt renamed' });
      setRenameDialog({ isOpen: false, prompt: null });
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [renameDialog.prompt, selectedPrompt]);

  const handleRenameCancel = useCallback(() => {
    setRenameDialog({ isOpen: false, prompt: null });
  }, []);

  // Render
  return (
    <div className={`flex flex-col h-full bg-gray-900 ${className}`}>
      {/* Header */}
      <header className="flex-shrink-0 border-b border-gray-800">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <ScrollText className="w-6 h-6 text-amber-400" />
            <h1 className="text-xl font-semibold text-white">System Prompts</h1>
          </div>

          <button
            type="button"
            onClick={handleCreateClick}
            disabled={isProcessing}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg disabled:opacity-50 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Prompt
          </button>
        </div>
      </header>

      {/* Status Messages */}
      {statusMessage && (
        <div className="px-6 pt-4">
          <StatusMessage
            type={statusMessage.type}
            message={statusMessage.message}
            onDismiss={() => setStatusMessage(null)}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Prompt List */}
        <div className="w-80 flex-shrink-0 border-r border-gray-800 flex flex-col">
          {/* Search */}
          <div className="p-4 border-b border-gray-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search prompts..."
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Prompt List */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
              </div>
            ) : (
              <PromptList
                prompts={prompts}
                selectedPrompt={selectedPrompt}
                onSelect={setSelectedPrompt}
                onDelete={handleDeleteClick}
                onRename={handleRenameClick}
                searchQuery={searchQuery}
                isProcessing={isProcessing}
              />
            )}
          </div>
        </div>

        {/* Right Panel - Editor */}
        <div className="flex-1 flex flex-col min-w-0">
          <PromptEditor
            prompt={editedPrompt}
            onChange={setEditedPrompt}
            onSave={handleSave}
            isSaving={isSaving}
            hasChanges={hasChanges}
          />
        </div>
      </div>

      {/* Create Dialog */}
      <CreatePromptDialog
        isOpen={createDialog}
        onConfirm={handleCreateConfirm}
        onCancel={handleCreateCancel}
        isProcessing={isProcessing}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        isOpen={deleteDialog.isOpen}
        promptName={deleteDialog.prompt?.name || ''}
        agentCount={deleteDialog.prompt?._count?.agents || 0}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isProcessing={isProcessing}
      />

      {/* Rename Dialog */}
      <RenameDialog
        isOpen={renameDialog.isOpen}
        currentName={renameDialog.prompt?.name || ''}
        onConfirm={handleRenameConfirm}
        onCancel={handleRenameCancel}
        isProcessing={isProcessing}
      />
    </div>
  );
}
